import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle2, HandHeart } from "lucide-react";
import SEO from "../components/SEO";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Referral() {
  const [form, setForm] = useState({
    referrer_name: "",
    referrer_phone: "",
    referrer_email: "",
    referrer_relationship: "",
    patient_name: "",
    patient_phone: "",
    patient_city: "",
    care_needed: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.referrer_name.trim()) e.referrer_name = "Your name is required";
    if (!form.referrer_phone.trim()) e.referrer_phone = "Your phone is required";
    if (!form.referrer_email.trim() || !/^\S+@\S+\.\S+$/.test(form.referrer_email))
      e.referrer_email = "Valid email is required";
    if (!form.patient_name.trim()) e.patient_name = "Patient's name is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/referrals`, form);
      setDone(true);
      toast.success("Referral submitted. Our care team will reach out shortly.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Submission failed. Please call us directly.";
      toast.error(typeof detail === "string" ? detail : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Send Your Referrals | 1st Home Care Services"
        description="Refer someone to 1st Home Care Services. Help a loved one or patient receive compassionate home care in Springfield, Fairfax, and Northern Virginia."
        canonical="https://1sthomecare.com/referral"
      />

      <section className="container-pad section-pad">
        <div className="max-w-3xl">
          <div className="text-xs font-bold uppercase tracking-widest text-brand-amber mb-3">
            Referral Form
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-brand-blue-dark text-balance">
            Send your referrals
          </h1>
          <p className="mt-6 text-lg text-gray-600 leading-relaxed">
            Help someone you know receive our care. Fill out the form below — a
            care coordinator will reach out within 24 hours.
          </p>
        </div>
      </section>

      <section className="container-pad pb-16 md:pb-24">
        {done ? (
          <div
            data-testid="referral-success"
            className="max-w-2xl bg-white rounded-2xl border border-gray-100 shadow-md p-10 text-center"
          >
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
            <h2 className="font-heading text-2xl font-semibold text-brand-blue-dark">
              Thank you for the referral
            </h2>
            <p className="text-gray-600 mt-2">
              We'll follow up with the patient and the referrer within 24 hours.
            </p>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            data-testid="referral-form"
            className="max-w-3xl bg-white rounded-2xl border border-gray-100 shadow-md p-6 md:p-10"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-brand-amber/10 text-brand-amber-dark flex items-center justify-center">
                <HandHeart className="w-6 h-6" />
              </div>
              <div>
                <h2 className="font-heading text-xl font-semibold text-brand-blue-dark">
                  Refer a patient or loved one
                </h2>
                <p className="text-xs text-gray-500">All fields with * are required.</p>
              </div>
            </div>

            <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-brand-blue mb-3">
              Your Information
            </h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="ref-name">Your Name *</Label>
                <Input id="ref-name" data-testid="ref-input-name" className="mt-1"
                  value={form.referrer_name} onChange={(e) => update("referrer_name", e.target.value)} />
                {errors.referrer_name && <p className="text-xs text-red-600 mt-1">{errors.referrer_name}</p>}
              </div>
              <div>
                <Label htmlFor="ref-phone">Your Phone *</Label>
                <Input id="ref-phone" type="tel" data-testid="ref-input-phone" className="mt-1"
                  value={form.referrer_phone} onChange={(e) => update("referrer_phone", e.target.value)} />
                {errors.referrer_phone && <p className="text-xs text-red-600 mt-1">{errors.referrer_phone}</p>}
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="ref-email">Your Email *</Label>
                <Input id="ref-email" type="email" data-testid="ref-input-email" className="mt-1"
                  value={form.referrer_email} onChange={(e) => update("referrer_email", e.target.value)} />
                {errors.referrer_email && <p className="text-xs text-red-600 mt-1">{errors.referrer_email}</p>}
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="ref-relationship">Your Relationship to Patient</Label>
                <Input id="ref-relationship" data-testid="ref-input-relationship" className="mt-1"
                  placeholder="e.g. Family member, Case manager, Friend"
                  value={form.referrer_relationship} onChange={(e) => update("referrer_relationship", e.target.value)} />
              </div>
            </div>

            <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-brand-blue mt-8 mb-3">
              Patient Information
            </h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="ref-pname">Patient's Name *</Label>
                <Input id="ref-pname" data-testid="ref-input-patient-name" className="mt-1"
                  value={form.patient_name} onChange={(e) => update("patient_name", e.target.value)} />
                {errors.patient_name && <p className="text-xs text-red-600 mt-1">{errors.patient_name}</p>}
              </div>
              <div>
                <Label htmlFor="ref-pphone">Patient's Phone</Label>
                <Input id="ref-pphone" type="tel" data-testid="ref-input-patient-phone" className="mt-1"
                  value={form.patient_phone} onChange={(e) => update("patient_phone", e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="ref-pcity">Patient's City</Label>
                <Input id="ref-pcity" data-testid="ref-input-patient-city" className="mt-1"
                  placeholder="Springfield, Fairfax, Arlington, etc."
                  value={form.patient_city} onChange={(e) => update("patient_city", e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="ref-care">Type of Care Needed</Label>
                <Input id="ref-care" data-testid="ref-input-care" className="mt-1"
                  placeholder="e.g. Skilled Nursing, Personal Care"
                  value={form.care_needed} onChange={(e) => update("care_needed", e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="ref-notes">Additional Notes</Label>
                <Textarea id="ref-notes" data-testid="ref-input-notes" rows={4} className="mt-1"
                  value={form.notes} onChange={(e) => update("notes", e.target.value)} />
              </div>
            </div>

            <Button type="submit" data-testid="referral-submit" disabled={submitting}
              className="w-full mt-6 h-12 bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold rounded-full">
              {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</>) : (<><Send className="w-4 h-4 mr-2" /> Submit Referral</>)}
            </Button>
          </form>
        )}
      </section>
    </>
  );
}
